import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { LangProvider } from '@/shared/i18n/LangProvider';
import { Layout } from '@/shared/ui/Layout';
import { shouldShowFoliageEditor, FoliageEditor } from '@/features/foliage-editor';

const Home = lazy(() => import('@/pages/Home'));
const About = lazy(() => import('@/pages/About'));
const ApiPlatform = lazy(() => import('@/pages/ApiPlatform'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const NotFound = lazy(() => import('@/pages/NotFound'));

/**
 * Minimal viewport-height placeholder shown while a route's lazy chunk is
 * resolving. Using `min-height: 100vh` on a brand-coloured background avoids
 * the CLS that an empty `<div>` would cause when the chunk is large.
 *
 * We expose an accessible status message for assistive tech but render
 * nothing visible — the route content arrives in tens of milliseconds on a
 * warm cache, so a spinner would just flash.
 */
function PageFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '100vh',
        background: 'var(--bp-dark-blue)',
      }}
    >
      <span
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Loading…
      </span>
    </div>
  );
}

export default function App() {
  const showEditor = shouldShowFoliageEditor();

  return (
    <LangProvider>
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="api-platform" element={<ApiPlatform />} />
              <Route path="privacy-policy" element={<Privacy />} />
              <Route path="terms-of-service" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
        {showEditor && (
          <Suspense fallback={null}>
            <FoliageEditor />
          </Suspense>
        )}
      </BrowserRouter>
    </LangProvider>
  );
}
